import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { List } from 'immutable';
import theme from '../common/theme';
import './fonts/prata_regular_normal';
import './fonts/roboto_bold_normal';
import './fonts/roboto_light_normal';
import './fonts/roboto_regular_normal';
import './fonts/roboto_regular_italic';

// DEPRECATED PDF GENERATOR
// REMOVE THIS ONCE SAFE TO DO

const setupDoc = (doc) => {
  const docX = doc.getBoundingClientRect().left;
  const docY = doc.getBoundingClientRect().top;
  const docRatio = 11 / 8.5;
  const docWidth = doc.getBoundingClientRect().width;
  const docHeight = docWidth * docRatio;
  const docMargin = (0.5 * docWidth) / 8.5;

  return {
    x: docX,
    y: docY,
    ratio: docRatio,
    width: docWidth,
    height: docHeight,
    margin: docMargin,
  };
};

const getPosition = (left, top, docX, docY) => ({
  x: left - docX,
  y: top - docY,
});

const getDocEls = (els) => {
  const isApproved = (el) => {
    switch (el.nodeName) {
      case 'H1':
      case 'H2':
      case 'H3':
      case 'H4':
      case 'P':
      case 'EM':
      case 'SPAN':
      case 'LI':
      case 'svg':
      case 'HR':
        return true;
      default:
        return false;
    }
  };

  return List(els).filter(isApproved);
};

const getType = (el) => {
  switch (el.nodeName) {
    case 'HR':
      return 'line';
    case 'LI':
      return 'list-item';
    case 'svg':
      return 'icon';
    default:
      return 'text';
  }
};

const prepEls = (els, doc, pdfWidth) => {
  const px2mm = (px) => (px * pdfWidth) / doc.width;

  return els.map((el) => {
    const elRect = el.getBoundingClientRect();
    const elPos = getPosition(elRect.left, elRect.top, doc.x, doc.y);

    return {
      type: getType(el),
      name: el.nodeName,
      node: el,
      text: el.innerText ? el.innerText : '',
      x: px2mm(elPos.x),
      y: px2mm(elPos.y),
      w: px2mm(elRect.width),
      h: px2mm(elRect.height),
    };
  });
};

const formatText = (el) => {
  let fontColor = theme.color.gray;
  let fontFamily = 'Roboto-Regular';
  let fontSize = '9';
  const fontStyle = 'normal';

  if (el !== 'reset') {
    switch (el.name) {
      case 'H1':
        fontColor = theme.color.grayDarker;
        fontFamily = 'Prata';
        fontSize = '29';
        break;
      case 'H2':
        fontColor = theme.color.grayDarker;
        fontFamily = 'Roboto-Bold';
        fontSize = '14.5';
        break;
      case 'H3':
        fontColor = theme.color.gray;
        fontFamily = 'Roboto-Bold';
        fontSize = '14.5';
        break;
      case 'H4':
        fontColor = theme.color.green;
        fontFamily = 'Roboto-Bold';
        fontSize = '13.5';
        break;
      case 'EM':
        fontFamily = 'Roboto-Italic';
        fontSize = '13.5';
        break;
      default:
      // no-op
    }
  }

  return {
    family: fontFamily,
    color: fontColor,
    size: fontSize,
    style: fontStyle,
  };
};

export const initializePdf = async (domEl) => {
  const pdf = new JsPDF('p', 'mm', 'letter');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  let pdfPages = 1;

  const doc = setupDoc(domEl);
  const allEls = Array.prototype.slice.call(domEl.getElementsByTagName('*'));
  const docEls = getDocEls(allEls);
  const pdfEls = prepEls(docEls, doc, pdfWidth);

  const px2mm = (px) => (px * pdfWidth) / doc.width;

  await Promise.all(
    pdfEls.map(async (el) => {
      let elY = el.y;

      // Set page initially
      pdf.setPage(1);

      // Check to see if items are past page height
      if (el.y >= pdfHeight - px2mm(doc.margin) * 2) {
        if (pdfPages <= 1) {
          pdf.addPage();
          pdfPages += 1;
        }

        pdf.setPage(2);
        elY = el.y - pdfHeight + px2mm(doc.margin) * 3;
      }

      if (el.type === 'text' || el.type === 'list-item') {
        // Style Font
        let font = formatText(el);
        pdf.setFont(font.family);
        pdf.setFontSize(font.size);
        pdf.setTextColor(font.color);
        pdf.setFontStyle(font.style);

        // Write text to PDF
        if (el.type === 'list-item') {
          pdf.text('•', el.x - 3, elY + el.h, { maxWidth: el.w });
        }
        pdf.text(el.text, el.x, elY + el.h, { maxWidth: el.w });

        // Reset Fonts
        font = formatText('reset');
        pdf.setFont(font.family);
        pdf.setFontSize(font.size);
        pdf.setTextColor(font.color);
        pdf.setFontStyle(font.style);
      } else if (el.type === 'line') {
        pdf.setDrawColor(theme.color.gray);
        pdf.line(el.x, elY + 2, el.x + el.w, elY + 2);
      } else if (el.type === 'icon') {
        // Setup SVG
        const svg = el.node;
        const svgWidth = svg.getBoundingClientRect().width;
        const svgHeight = svg.getBoundingClientRect().height;
        svg.setAttribute('width', `${svgWidth * 2}px`);
        svg.setAttribute('height', `${svgHeight * 2}px`);

        // Setup Canvas
        const svgCanvas = document.createElement('canvas');
        svgCanvas.getContext('2d');
        svgCanvas.width = svgWidth * 2;
        svgCanvas.height = svgHeight * 2;

        await html2canvas(svg, {
          canvas: svgCanvas,
          scale: 2,
          width: svgWidth,
          height: svgHeight,
        }).then((canvas) => {
          // Convert canvas to PNG
          const img = canvas.toDataURL('image/png', 1);

          if (el.y >= pdfHeight - px2mm(doc.margin) * 2) {
            pdf.setPage(2);
            elY = el.y - pdfHeight + px2mm(doc.margin) * 3;
          } else {
            pdf.setPage(1);
            elY = el.y;
          }
          // Place PNG on page
          pdf.addImage(img, 'PNG', el.x, elY, px2mm(svgWidth), px2mm(svgHeight));
        });
      }
    }),
  );

  return pdf;
};

export default initializePdf;
